import React, { ElementType } from 'react';
import { styled } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import { Colors } from 'library';
import { BaseButton } from './BaseButton';
import { TextTooltip } from './Tooltip';

interface IconButtonProps {
    icon: SvgIconComponent | ElementType<{ sx?: unknown }>;
    onClick?: (e: React.MouseEvent) => void;
    disabled?: boolean;
    selected?: boolean;
    variant?: 'default' | 'filled';
    size?: number;
    iconSize?: number;
    tooltip?: string;
    className?: string;
}

export function IconButton({
    icon: Icon,
    onClick,
    disabled,
    selected,
    variant = 'default',
    size = 36,
    iconSize = 22,
    tooltip,
    className,
}: IconButtonProps) {
    const button = (
        <Button
            onClick={onClick}
            disabled={disabled}
            $selected={!!selected}
            $filled={variant === 'filled'}
            className={className}
            style={{ width: size, height: size }}
        >
            <Icon sx={{ fontSize: iconSize }} />
        </Button>
    );

    return tooltip ? <TextTooltip title={tooltip}>{button}</TextTooltip> : button;
}

const Button = styled(BaseButton, {
    shouldForwardProp: (prop) => prop !== '$selected' && prop !== '$filled',
})<{ $selected: boolean; $filled: boolean }>(({ $selected, $filled }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderRadius: $filled ? '50%' : 6,
    backgroundColor: $filled ? Colors.primary : $selected ? Colors.fill : 'transparent',
    color: $filled ? Colors.white : $selected ? Colors.primary : Colors.textSecondary,
    '&:hover:not(:disabled)': {
        backgroundColor: $filled ? Colors.primaryDarkened : $selected ? Colors.fill : Colors.surface,
    },
}));
